import { useCallback, useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import { Navigate } from "react-router";

import { EmptystateGridTilesColored } from "components/icons/generated";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import TabPanel from "ds/components/TabPanel";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { Column, ColumnId, RenderItemContentProps } from "components/DragDropList/types";
import { AccountContext } from "views/AccountWrapper";

import DragDropList from "../../../components/DragDropList";
import { DefaultDashboardTabs } from "./constants";
import { DashboardContext } from "./context";
import { getAllDashboardWidgets } from "./helpers";
import DashboardOverviewColumn from "./OverviewColumn";
import styles from "./styles.module.css";
import { Widget } from "./types";
import useCurrentTab from "./useCurrentTab";
import DashboardCard from "./Card";

const DashboardList = () => {
  const { openManageDrawer } = useTypedContext(DashboardContext);
  const currentTab = useCurrentTab();
  const {
    numberOfDeploymentsWidgetFrontend,
    approvalPolicyEvaluationsWidgetFrontend,
    opentofuBackend,
  } = useTypedFlags();

  const { configLoading, config, updateTabColumnsConfig } = useTypedContext(DashboardContext);
  const { viewer } = useTypedContext(AccountContext);

  const currentTabTitle = config[currentTab]?.title;

  useTitle(`${currentTabTitle ? `${currentTabTitle} · ` : ""}Dashboard`);

  const firstColumn = config[currentTab]?.left;
  const secondColumn = config[currentTab]?.right;

  const updateConfig = useCallback(
    (columns: { first: Column<Widget>; second: Column<Widget> }) =>
      updateTabColumnsConfig(currentTab, {
        left: columns.first,
        right: columns.second,
      }),
    [currentTab, updateTabColumnsConfig]
  );

  // Hide an item in a specific column
  const hideItem = useCallback(
    (targetColumn: ColumnId, id: Widget) => {
      const result = { first: firstColumn, second: secondColumn };

      result[targetColumn] = result[targetColumn].map((item) =>
        item.value === id ? { ...item, hidden: true } : item
      );

      updateConfig(result);
    },
    [firstColumn, secondColumn, updateConfig]
  );

  const allWidgets = useMemo(
    () =>
      getAllDashboardWidgets(
        {
          numberOfDeploymentsWidgetFrontend,
          approvalPolicyEvaluationsWidgetFrontend,
          opentofuBackend,
        },
        viewer.admin
      ),
    [
      numberOfDeploymentsWidgetFrontend,
      approvalPolicyEvaluationsWidgetFrontend,
      opentofuBackend,
      viewer.admin,
    ]
  );

  const renderItemContent = useCallback(
    ({
      id,
      handleAttributes,
      handleListeners,
      handleRef,
      disabled,
      columnId,
      dragging,
    }: RenderItemContentProps<Widget, undefined>) => {
      const value = allWidgets[id as Widget];

      if (!value) {
        return null;
      }

      const Component = value.component;
      const HeaderActions = value.headerActions;

      return (
        <DashboardCard
          handleAttributes={handleAttributes}
          handleListeners={handleListeners}
          handleRef={handleRef}
          disabled={disabled}
          columnId={columnId}
          dragging={dragging}
          hideItem={hideItem}
          id={id}
          title={value.title}
          infoTooltip={value.infoTooltip}
          moreActions={value.moreActions}
          headerActions={HeaderActions && <HeaderActions />}
        >
          <Component />
        </DashboardCard>
      );
    },
    [allWidgets, hideItem]
  );

  if (configLoading) {
    return (
      <TabPanel isActive id={currentTab} padding="x-large" gap="x-large">
        <Skeleton width="calc(50% - 161px)" height={262} borderRadius={12} />
        <Skeleton width="calc(50% - 161px)" height={156} borderRadius={12} />
        <Skeleton width="calc(50% - 161px)" height={254} borderRadius={12} />
      </TabPanel>
    );
  }

  const dragDropListConfig = config[currentTab];

  if (!dragDropListConfig) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <TabPanel
      isActive
      id={currentTab}
      direction="row"
      fullWidth
      className={styles.dashboardListWrapper}
      grow="1"
    >
      <Box className={styles.dashboardList}>
        {dragDropListConfig && (
          <Box grow="1" className={styles.dashboardListContent}>
            <DragDropList
              itemNoBorder
              itemSize="medium"
              renderItemContent={renderItemContent}
              key={currentTab}
              onChange={updateConfig}
              columnMode={dragDropListConfig.columnMode}
              firstColumn={firstColumn}
              secondColumn={secondColumn}
              itemClassName={styles.dashboardListItem}
              emptyState={
                <EmptyState
                  icon={EmptystateGridTilesColored}
                  title="Dashboard without widgets is no fun!"
                  caption={
                    <>
                      Click on the cog icon in the top right section of the header <br /> to manage
                      the widgets displayed on this view.
                    </>
                  }
                >
                  <Button
                    variant="secondary"
                    onPress={() =>
                      openManageDrawer(undefined, { method: "Empty state button click" })
                    }
                  >
                    Manage dashboard
                  </Button>
                </EmptyState>
              }
            />
          </Box>
        )}
        {currentTab === DefaultDashboardTabs.Overview && <DashboardOverviewColumn />}
      </Box>
    </TabPanel>
  );
};

export default DashboardList;
